import { useState, useRef, useEffect, memo } from "react";
import { eventBus } from "../../../../utils/eventBus";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend
} from "recharts";
import api from "../../../../api/careiqapi";
import { getPracticeId } from "../../../../config/practice";

const BR = "#E8653A";
const G = { 50: "#F1EFE8", 100: "#D3D1C7", 200: "#B4B2A9", 400: "#888780", 600: "#5F5E5A", 900: "#2C2C2A" };

function bpC(s) { return !s ? { l: "--", c: G[400] } : s < 120 ? { l: "Normal", c: "#1D9E75" } : s < 130 ? { l: "Elevated", c: "#BA7517" } : s < 140 ? { l: "Stage 1", c: "#D85A30" } : { l: "Stage 2", c: "#E24B4A" }; }
function plC(v) { return !v ? { l: "--", c: G[400] } : v >= 60 && v <= 100 ? { l: "regular", c: "#1D9E75" } : { l: "abnormal", c: "#E24B4A" }; }
function bmiC(v) { return !v || isNaN(v) ? { l: "--", c: G[400] } : v < 18.5 ? { l: "Underweight", c: "#378ADD" } : v < 25 ? { l: "Normal", c: "#1D9E75" } : v < 30 ? { l: "Overweight", c: "#BA7517" } : v < 35 ? { l: "Obese I", c: "#D85A30" } : { l: "Obese II", c: "#E24B4A" }; }

function Spark({ data, color, w = 44, h = 14 }) {
  if (!data || data.length < 2) return null;
  const validData = data.filter(d => d != null && !isNaN(d));
  if (validData.length < 2) return null;
  const mn = Math.min(...validData), mx = Math.max(...validData), rng = mx - mn || 1;
  const pts = validData.map((v, i) => `${2 + (i / (validData.length - 1)) * (w - 4)},${2 + (1 - (v - mn) / rng) * (h - 4)}`).join(" ");
  const last = pts.split(" ").pop().split(",");
  return (
    <svg width={w} height={h} style={{ display: "block", flexShrink: 0 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.45" />
      <circle cx={parseFloat(last[0])} cy={parseFloat(last[1])} r="2" fill={color} opacity="0.8" />
    </svg>
  );
}

function Dot(props) {
  const { cx, cy, value, stroke } = props;
  if (cx == null || cy == null) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={3.5} fill={stroke} stroke="#fff" strokeWidth={1.5} />
      <text x={cx} y={cy - 10} textAnchor="middle" fontSize={10} fontWeight={600} fill={stroke}>{value}</text>
    </g>
  );
}

const TABS = [
  { key: "bp", label: "BP" },
  { key: "pulse", label: "PULSE" },
  { key: "weight", label: "WEIGHT" },
  { key: "spo2", label: "O₂ SAT" },
];

function VitalRow({ label, value, unit, sc, trend, suffix, active, onClick, rowRef }) {
  return (
    <div ref={rowRef} onClick={onClick} style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "8px 4px", cursor: onClick ? "pointer" : "default", borderRadius: 8,
      borderBottom: "1px solid #F1F3F7",
      background: active ? `${BR}0A` : "transparent",
      borderRight: active ? `2.5px solid ${BR}` : "2.5px solid transparent",
      transition: "all 0.12s",
      position: "relative",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <div style={{ width: 6, height: 6, borderRadius: "50%", background: sc, flexShrink: 0 }} />
        <span style={{ fontSize: 13.5, fontWeight: 500, color: "#6B7487", minWidth: 44 }}>{label}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {trend && <Spark data={trend} color={sc} w={56} h={18} />}
        <div style={{ textAlign: "right", minWidth: 92 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "#151E36" }}>{value}</span>
          {unit && <span style={{ fontSize: 12, color: "#8A93A5", marginLeft: 3 }}>{unit}</span>}
          {suffix && suffix !== "--" && <div style={{ fontSize: 11.5, fontWeight: 500, color: sc, marginTop: 1 }}>{suffix}</div>}
        </div>
      </div>
    </div>
  );
}

function DetailPanel({ activeTab, onTabChange, onClose, panelPos, READINGS }) {
  // Chart the last 5 readings THAT HAVE the active metric — slicing the
  // last 5 overall starved sparse metrics (O₂ sat is recorded far less
  // often than pulse/weight, so its chart came up empty).
  const TAB_KEYS = { bp: ["sys", "dia"], pulse: ["pulse"], weight: ["weight"], spo2: ["spo2"] };
  const chartData = READINGS
    .filter(r => (TAB_KEYS[activeTab] || []).some(k => r[k] != null))
    .slice(-5)
    .map(r => {
      const d = new Date(r.date);
      const safeDate = isNaN(d) ? r.date : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return {
        date: safeDate,
        sys: r.sys, dia: r.dia, pulse: r.pulse, weight: r.weight, spo2: r.spo2,
      };
    });

  const gP = { strokeDasharray: "3 3", stroke: "rgba(136,135,128,0.12)" };
  const xP = { dataKey: "date", tick: { fontSize: 10, fill: G[400] }, tickLine: false, axisLine: { stroke: G[200] } };
  const yT = { fontSize: 10, fill: G[400] };
  const ttS = { fontSize: 11, borderRadius: 6, border: `1px solid ${G[200]}`, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" };

  const renderChart = () => {
    if (activeTab === "bp") return (
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={chartData} margin={{ top: 22, right: 16, bottom: 2, left: -16 }}>
          <CartesianGrid {...gP} /><XAxis {...xP} /><YAxis domain={['auto', 'auto']} tick={yT} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={ttS} />
          <ReferenceLine y={140} stroke="#E24B4A" strokeDasharray="4 4" strokeOpacity={0.3} />
          <ReferenceLine y={90} stroke="#E24B4A" strokeDasharray="4 4" strokeOpacity={0.3} />
          <Line type="monotone" dataKey="sys" stroke={BR} strokeWidth={2} name="Systolic" dot={<Dot stroke={BR} />} activeDot={{ r: 4 }} connectNulls={true} />
          <Line type="monotone" dataKey="dia" stroke="#1e3a5f" strokeWidth={2} name="Diastolic" dot={<Dot stroke="#1e3a5f" />} activeDot={{ r: 4 }} connectNulls={true} />
          <Legend iconSize={10} iconType="line" wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    );
    if (activeTab === "pulse") return (
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={chartData} margin={{ top: 22, right: 16, bottom: 2, left: -16 }}>
          <CartesianGrid {...gP} /><XAxis {...xP} /><YAxis domain={['auto', 'auto']} tick={yT} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={ttS} />
          <Line type="monotone" dataKey="pulse" stroke="#E24B4A" strokeWidth={2} name="Pulse (bpm)" dot={<Dot stroke="#E24B4A" />} activeDot={{ r: 4 }} connectNulls={true} />
        </LineChart>
      </ResponsiveContainer>
    );
    if (activeTab === "weight") return (
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={chartData} margin={{ top: 22, right: 16, bottom: 2, left: -16 }}>
          <CartesianGrid {...gP} /><XAxis {...xP} /><YAxis domain={['auto', 'auto']} tick={yT} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={ttS} />
          <Line type="monotone" dataKey="weight" stroke="#BA7517" strokeWidth={2} name="Weight (lbs)" dot={<Dot stroke="#BA7517" />} activeDot={{ r: 4 }} connectNulls={true} />
        </LineChart>
      </ResponsiveContainer>
    );
    if (activeTab === "spo2") {
      if (!chartData.length) return <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", color: G[400], fontSize: 12 }}>No O₂ data recorded</div>;
      return (
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartData} margin={{ top: 22, right: 16, bottom: 2, left: -16 }}>
            <CartesianGrid {...gP} /><XAxis {...xP} /><YAxis domain={['auto', 100]} tick={yT} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={ttS} />
            <ReferenceLine y={92} stroke="#E24B4A" strokeDasharray="4 4" strokeOpacity={0.3} />
            <Line type="monotone" dataKey="spo2" stroke="#2E86AB" strokeWidth={2} name="O₂ Sat (%)" dot={<Dot stroke="#2E86AB" />} activeDot={{ r: 4 }} connectNulls={true} />
          </LineChart>
        </ResponsiveContainer>
      );
    }
    return <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", color: G[400], fontSize: 12 }}>No data recorded</div>;
  };

  return (
    <div style={{ position: "fixed", left: panelPos.left, top: panelPos.top, width: 380, zIndex: 999999, minWidth: 0 }}>
      {/* Connector nub */}
      <div style={{
        position: "absolute", left: -8, top: panelPos.connY - 6, width: 16, height: 12,
        zIndex: 2, pointerEvents: "none", transition: "top 0.2s ease",
      }}>
        <svg width="16" height="12" viewBox="0 0 16 12">
          <path d="M16 0C8 0 8 6 0 6C8 6 8 12 16 12Z" fill="#fff" />
          <path d="M16 0C8 0 8 6 0 6" fill="none" stroke="rgba(136,135,128,0.18)" strokeWidth="1" />
          <path d="M0 6C8 6 8 12 16 12" fill="none" stroke="rgba(136,135,128,0.18)" strokeWidth="1" />
        </svg>
      </div>

      <div style={{
        background: "#fff", borderRadius: 10, border: "1px solid rgba(136,135,128,0.18)",
        display: "flex", flexDirection: "column", overflowY: "auto", overflowX: "hidden",
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        animation: "panelIn 0.2s ease",
        maxHeight: "90vh"
      }}>
        {/* Tab bar + close */}
        <div style={{ display: "flex", alignItems: "stretch", borderBottom: "1px solid rgba(136,135,128,0.1)" }}>
          {TABS.map(t => {
            const act = t.key === activeTab;
            return (
              <button key={t.key} onClick={() => onTabChange(t.key)} style={{
                flex: 1, padding: "8px 2px", border: "none", cursor: "pointer",
                background: act ? BR : "transparent", color: act ? "#fff" : G[600],
                fontSize: 10, fontWeight: 600, letterSpacing: 0.4,
                borderRadius: act ? "8px 8px 0 0" : 0, transition: "all 0.12s",
              }}>{t.label}</button>
            );
          })}
          <button onClick={onClose} style={{
            width: 32, border: "none", background: "transparent", cursor: "pointer",
            color: G[400], fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
            borderLeft: "1px solid rgba(136,135,128,0.1)", flexShrink: 0,
          }}>×</button>
        </div>

        {/* Chart */}
        <div style={{ padding: "4px 6px 0" }}>{renderChart()}</div>

        {/* Compact table */}
        <div style={{ padding: "8px 10px 10px", overflowY: "auto", maxHeight: 180 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr>
                {["Date", "BP", "Pulse", "Wt", "O₂"].map((h, i) => (
                  <th key={i} style={{
                    textAlign: "left", padding: "4px 5px", color: BR, fontWeight: 500,
                    borderBottom: "1px solid rgba(136,135,128,0.12)", whiteSpace: "nowrap", fontSize: 10,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...READINGS].reverse().slice(0,5).map((r, idx) => {
                const d = new Date(r.date);
                const fD = isNaN(d) ? r.date : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
                return (
                  <tr key={idx} style={{ borderBottom: "1px solid rgba(136,135,128,0.05)" }}>
                    <td style={{ padding: "5px 5px", color: G[400], whiteSpace: "nowrap" }}>{fD}</td>
                    <td style={{ padding: "5px 5px", fontWeight: 500, color: G[900] }}>
                      {r.sys || r.dia ? `${r.sys || '--'}/${r.dia || '--'}` : '--'}
                    </td>
                    <td style={{ padding: "5px 5px", color: G[900] }}>{r.pulse || "--"}</td>
                    <td style={{ padding: "5px 5px", color: G[900] }}>{r.weight || "--"}</td>
                    <td style={{ padding: "5px 5px", color: G[400] }}>{r.spo2 ?? "--"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button 
            onClick={() => eventBus.emit("navigateToPriorVitals", { tab: 3, internalTab: 1 })}
            style={{ marginTop: 15, width: "100%", padding: "8px 0", background: "none", border: "none", color: "#E17954", fontSize: 13, fontWeight: 600, cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            View All Vitals
          </button>
        </div>
      </div>
    </div>
  );
}

function CareIQVitals({ patientId, currentVitals }) {
  const [READINGS, setReadings] = useState([]);
  const [sel, setSel] = useState(null);
  const rowRefs = useRef({});
  const containerRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, connY: 40 });

  useEffect(() => {
    console.log("CareIQVitals useEffect[patientId] triggered. patientId:", patientId);
    if (!patientId) {
      console.log("CareIQVitals Early Return! patientId is falsy.");
      return;
    }
    let cancelled = false;
    const loadData = async () => {
      try {
        const practiceId = getPracticeId();
        const resp = await api.post(`/v1/fhir-mirror/${practiceId}/vitals/patient/${patientId}/athena/get`, { department_id: 1 });
        if (!cancelled && resp.data) {
          const d = resp.data;
          const readingsMap = {};

          const processArray = (dateArr, valArr, key) => {
            if (!dateArr || !valArr) return;
            dateArr.forEach((dateStr, i) => {
              if (!dateStr) return;
              const isoLike = String(dateStr).trim();
              if (!readingsMap[isoLike]) readingsMap[isoLike] = { date: isoLike };
              let val = valArr[i];
              if (typeof val === 'string') val = parseFloat(val);
              if (!isNaN(val)) readingsMap[isoLike][key] = val;
            });
          };

          processArray(d.systolicdatelabel, d.systolic, 'sys');
          processArray(d.diastolicdatelabel, d.diastolic, 'dia');
          processArray(d.pulsedatelabel, d.pulse, 'pulse');
          processArray(d.wtdatelabel, d.weight, 'weight');
          processArray(d.O2Satdatelabel, d.O2Sat, 'spo2');

          const newReadings = Object.values(readingsMap).sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime());
          console.log("CareIQVitals Parsed READINGS:", newReadings);
          setReadings(newReadings);
        } else {
          console.log("CareIQVitals No Data Received. resp:", resp);
        }
      } catch (err) {
        console.log("CareIQVitals Fetch Error:", err);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [patientId]);

  useEffect(() => {
    if (sel && rowRefs.current[sel]) {
      const rowRect = rowRefs.current[sel].getBoundingClientRect();
      let topPos = rowRect.top - 80;
      
      const estimatedModalHeight = 420;
      if (topPos + estimatedModalHeight > window.innerHeight) {
        topPos = Math.max(20, window.innerHeight - estimatedModalHeight - 20);
      }
      if (topPos < 20) topPos = 20; 

      let connY = (rowRect.top - topPos) + rowRect.height / 2;
      const actualMaxConnY = window.innerHeight < estimatedModalHeight ? window.innerHeight - 60 : estimatedModalHeight - 30;
      if (connY > actualMaxConnY) connY = actualMaxConnY;
      if (connY < 20) connY = 20;

      setPanelPos({
        top: topPos,
        left: rowRect.right + 16,
        connY: connY
      });
    }
  }, [sel]);

  const toggle = (k) => setSel(sel === k ? null : k);

  const curSys = currentVitals?.bp_mm ? parseInt(currentVitals.bp_mm) : null;
  const curDia = currentVitals?.bp_hgg ? parseInt(currentVitals.bp_hgg) : null;
  const curPulse = currentVitals?.plus_bpm ? parseInt(currentVitals.plus_bpm) : null;
  const curWeightStr = currentVitals?.weight ? currentVitals.weight.replace(/[^0-9.]/g, '') : null;
  const curWeight = curWeightStr ? parseFloat(curWeightStr) : null;
  const curSpO2 = currentVitals?.o2_1 || currentVitals?.o2_2 ? parseFloat(currentVitals?.o2_1 || currentVitals?.o2_2) : null;

  const ft = parseInt(currentVitals?.height_ft) || (currentVitals?.height_in ? 0 : 5);
  const inch = parseInt(currentVitals?.height_in) || 0;
  const HT_IN = ft * 12 + inch;
  const BMI_V = curWeight && HT_IN ? ((curWeight / (HT_IN * HT_IN)) * 703).toFixed(1) : "--";

  const CUR = { sys: curSys, dia: curDia, pulse: curPulse, weight: curWeight, spo2: curSpO2 };

  const bpSt = bpC(CUR.sys);
  const plSt = plC(CUR.pulse);
  const bmiSt = bmiC(parseFloat(BMI_V));

  const TRENDS = READINGS.slice(-5);
  // Per-metric sparkline: last 5 readings that HAVE the metric, so sparse
  // vitals (O₂ sat) still draw a trend instead of an empty spark.
  const trendOf = (k) => READINGS.filter((r) => r[k] != null).slice(-5).map((r) => r[k]);

  return (
    <div ref={containerRef} style={{ fontFamily: "inherit" }}>
      <style>{`
        @keyframes panelIn {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", position: "relative", marginBottom: "8px" }}>
        {/* ── Sidebar ── */}
        <div style={{ width: "100%", flexShrink: 1 }}>
          {/* The sidebar card supplies the "Vitals" heading and the frame. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <VitalRow label="BP" value={CUR.sys ? `${CUR.sys}/${CUR.dia}` : "--"} unit="mmHg"
              sc={bpSt.c} trend={trendOf('sys')} suffix={bpSt.l}
              active={sel === "bp"} onClick={() => toggle("bp")}
              rowRef={el => rowRefs.current.bp = el} />

            <VitalRow label="Pulse" value={CUR.pulse || "--"} unit="bpm"
              sc={plSt.c} trend={trendOf('pulse')} suffix={plSt.l}
              active={sel === "pulse"} onClick={() => toggle("pulse")}
              rowRef={el => rowRefs.current.pulse = el} />

            <VitalRow label="SpO2" value={CUR.spo2 || "--"} unit="%" sc={G[200]}
              active={sel === "spo2"} onClick={() => toggle("spo2")} trend={trendOf('spo2')}
              rowRef={el => rowRefs.current.spo2 = el} />

            <VitalRow label="Weight" value={CUR.weight || "--"} unit="lbs"
              sc="#BA7517" trend={trendOf('weight')}
              active={sel === "weight"} onClick={() => toggle("weight")}
              rowRef={el => rowRefs.current.weight = el} />

            {/* BMI — above Height */}
            <div style={{
              margin: "8px 0 4px", padding: "8px 12px", borderRadius: 10,
              background: `${bmiSt.c}10`, border: `1px solid ${bmiSt.c}18`,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div>
                <span style={{ fontSize: 13, color: bmiSt.c, fontWeight: 500 }}>BMI</span>
                <span style={{ fontSize: 15, fontWeight: 600, color: bmiSt.c, marginLeft: 8 }}>{BMI_V}</span>
              </div>
              <span style={{
                fontSize: 12, fontWeight: 500, color: bmiSt.c,
                background: `${bmiSt.c}14`, padding: "2px 8px", borderRadius: 6,
              }}>{bmiSt.l}</span>
            </div>

            {/* Height — last, static */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "8px 4px 2px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: G[200] }} />
                <span style={{ fontSize: 13.5, fontWeight: 500, color: "#6B7487" }}>Height</span>
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: "#151E36" }}>{ft}'{inch}"</span>
                <div style={{ fontSize: 11.5, color: "#8A93A5" }}>standing</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Detail Panel ── */}
        {sel && (
          <DetailPanel
            activeTab={sel === "wt" ? "weight" : sel}
            onTabChange={(k) => setSel(k)}
            onClose={() => setSel(null)}
            panelPos={panelPos}
            READINGS={READINGS}
          />
        )}
      </div>
    </div>
  );
}

export default memo(CareIQVitals);
